<script setup lang="ts">
/**
 * 공통 Contact 등록 모달 (모든 PreSet 패널에서 재사용).
 *   SG 의 기존 패턴(Contact 페이지를 :is-modal 로 v-dialog 임베드)에 맞춰,
 *   ① 개인/법인 타입 선택 → ② 해당 Contact 생성 페이지(Individual/Corporate)를 모달 모드로 임베드.
 *   저장 로직은 페이지 한 곳에만 존재(중복 없음). 생성 성공 시 created({contactCd,type,data}) 를 패널로 전달.
 *
 * 사용:
 *   <ContactRegisterModal v-model="open" @created="onContactCreated" />
 *   forceType 를 주면 타입 선택을 건너뛰고 해당 폼으로 바로 진입.
 */
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import ContactCreateIndividualPage from '@/features/Contact/views/ContactCreateIndividualPage.vue';
import ContactCreateCorporatePage from '@/features/Contact/views/ContactCreateCorporatePage.vue';

type ContactType = 'individual' | 'corporate';

const props = withDefaults(defineProps<{
  modelValue: boolean;
  /** 지정 시 타입 선택 단계를 건너뛰고 해당 폼으로 바로 진입. */
  forceType?: ContactType | null;
  title?: string;
  /** (선택) 2단계 등록 폼 초기값 — 생성 페이지 initialData 로 전달. 없으면 기존과 동일 */
  initialData?: Record<string, unknown> | null;
  /** (선택) 2단계 등록 폼 위 안내 문구. 없으면 표시 안 함 */
  banner?: string;
  /** (선택) 개인 Contact 이메일 필수 생략 — S2000 Convert 전용. 기본 false = 기존처럼 필수 */
  emailOptional?: boolean;
}>(), {
  forceType: null,
  title: '',
  initialData: null,
  banner: '',
  emailOptional: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'created', payload: { contactCd: string; type: ContactType; data: any }): void;
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

/** 번역이 없으면(키 그대로 반환) fallback 사용. en.json 에 일부 키 미존재 → fallback 필요. */
function tr(key: string, fallback: string): string {
  const v = t(key);
  return v && v !== key ? v : fallback;
}

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const selectedType = ref<ContactType | null>(null);

// 모달 오픈 시 타입 초기화(forceType 있으면 바로 해당 폼).
watch(visible, (v) => {
  if (v) selectedType.value = props.forceType ?? null;
});

const dialogTitle = computed(() => {
  if (props.title) return props.title;
  if (selectedType.value === 'individual') return tr('contact.Registrable Individual', 'Register Individual Contact');
  if (selectedType.value === 'corporate') return tr('contact.Registrable Corporate', 'Register Corporate Contact');
  return tr('contact.Register Contact', 'Register Contact');
});

function close() {
  visible.value = false;
}

function backToSelect() {
  // forceType 모드면 뒤로 갈 단계가 없으니 닫는다.
  if (props.forceType) close();
  else selectedType.value = null;
}

function onCreated(payload: { contactCd: string; data: any }) {
  emit('created', {
    contactCd: payload.contactCd,
    type: (selectedType.value || 'individual') as ContactType,
    data: payload.data,
  });
  visible.value = false;
}
</script>

<template>
  <v-dialog v-model="visible" max-width="960" persistent scrollable>
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center pa-4 pb-3">
        <v-btn
          v-if="selectedType && !forceType"
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          class="me-1"
          @click="backToSelect"
        />
        <v-icon v-else color="primary" class="me-2">mdi-account-plus-outline</v-icon>
        <span class="text-h6 font-weight-bold">{{ dialogTitle }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" size="small" @click="close" />
      </v-card-title>
      <v-divider />

      <!-- Step 1: 개인/법인 타입 선택 -->
      <v-card-text v-if="!selectedType" class="pa-6">
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ tr('contact.Select contact type', 'Select the type of contact to register.') }}
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-card variant="outlined" class="pa-6 d-flex flex-column align-center text-center contact-type-card"
              @click="selectedType = 'individual'">
              <v-icon size="44" color="primary" class="mb-3">mdi-account</v-icon>
              <div class="text-subtitle-1 font-weight-bold mb-1">{{ tr('contact.Individual', 'Individual') }}</div>
              <div class="text-caption text-medium-emphasis">{{ tr('contact.Registrable Individual', 'A natural person') }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card variant="outlined" class="pa-6 d-flex flex-column align-center text-center contact-type-card"
              @click="selectedType = 'corporate'">
              <v-icon size="44" color="primary" class="mb-3">mdi-domain</v-icon>
              <div class="text-subtitle-1 font-weight-bold mb-1">{{ tr('contact.Corporate', 'Corporate') }}</div>
              <div class="text-caption text-medium-emphasis">{{ tr('contact.Registrable Corporate', 'A company / body corporate') }}</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Step 2: 선택한 타입의 생성 폼(페이지 임베드) -->
      <v-card-text v-else class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <v-alert v-if="banner" type="info" variant="tonal" density="compact" class="mb-3">{{ banner }}</v-alert>
        <ContactCreateIndividualPage
          v-if="selectedType === 'individual'"
          :is-modal="true"
          :initial-data="initialData as any"
          :email-optional="emailOptional"
          @created="onCreated"
          @cancel="backToSelect"
        />
        <ContactCreateCorporatePage
          v-else
          :is-modal="true"
          :initial-data="initialData as any"
          @created="onCreated"
          @cancel="backToSelect"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.contact-type-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.contact-type-card:hover {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
}
</style>
